import { useEffect } from "react";

import { isInsideMobileWebView } from "@/utils/mobileBridgeAuth";

import type { DocumentModel } from "@/models/DocumentModel";

/** Keep the caret at least this far above the keyboard's top edge. */
const MARGIN = 120;
/** Keep the caret at least this far below the viewport's top edge. */
const TOP_MARGIN = 40;
/** Ignore sub-pixel reveals to avoid pointless scrolls. */
const MIN_DELTA = 2;
/** Debounce: let the keyboard/selection burst settle, then scroll once. */
const SETTLE_MS = 120;
/** Min viewport-height drop (px) below the keyboard-closed height to treat as "keyboard open". */
const KEYBOARD_MIN = 120;

/**
 * Mobile keyboard-aware scrolling (our own — the native WKWebView auto-scroll is
 * disabled via a react-native-webview patch).
 *
 * The native host (apps/mobile) wraps the WebView in a `KeyboardAvoidingView`, so
 * when the keyboard + its toolbar open the WebView *shrinks* to the area above them.
 * `window.innerHeight` therefore already excludes the keyboard — "below the fold" is
 * exactly "behind the keyboard", and we don't need the keyboard height from native.
 *
 * Two triggers, deliberately not redundant:
 *  - viewport *shrink* (resize) — the keyboard just opened; lift the caret. This is
 *    the only signal for the open animation, since focus/selection fire before it.
 *  - caret moved while open (selectionchange) — keep the caret visible as you type.
 *
 * We never scroll on a plain scroll (it changes neither the viewport size nor the
 * selection) nor once the keyboard closes — so the user can scroll away freely
 * without the page snapping back.
 */
export function useKeyboardAwareScroll(doc: DocumentModel) {
  useEffect(() => {
    if (typeof window === "undefined" || !isInsideMobileWebView()) return undefined;

    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const viewportHeight = () => window.visualViewport?.height ?? window.innerHeight;
    // Tallest viewport seen = the keyboard-closed height. The keyboard is "open" only
    // when the current height is meaningfully below it.
    let fullHeight = viewportHeight();
    const keyboardOpen = () => viewportHeight() < fullHeight - KEYBOARD_MIN;

    const isEditing = () => {
      const active = document.activeElement;
      const editorEl = doc.editor.tiptap?.options.element;
      return (
        active instanceof HTMLElement &&
        editorEl instanceof HTMLElement &&
        editorEl.contains(active)
      );
    };

    // Viewport-relative rect of what must stay visible: the focused math input, else
    // the text caret.
    const caretRect = (): { top: number; bottom: number } | null => {
      const active = document.activeElement;
      if (active instanceof HTMLInputElement && isEditing()) {
        return active.getBoundingClientRect();
      }
      const { tiptap } = doc.editor;
      if (!tiptap) return null;
      try {
        return tiptap.view.coordsAtPos(tiptap.state.selection.head);
      } catch {
        return null;
      }
    };

    const reveal = () => {
      // Only while the keyboard is open. Once it closes the viewport grows back, so a
      // late event must not scroll us back to where the caret was. Dismissing the keyboard
      // blurs first and shrinks later, so a blur-time selectionchange still sees it "open".
      if (!keyboardOpen() || !isEditing()) return;
      const rect = caretRect();
      if (rect === null) return;
      const below = rect.bottom - (viewportHeight() - MARGIN);
      const above = rect.top - TOP_MARGIN;
      // In a viewport too short for both margins, the top one wins; otherwise each reveal
      // would undo the previous one and the page would oscillate.
      const delta = Math.min(above, Math.max(below, 0));
      if (Math.abs(delta) > MIN_DELTA) {
        window.scrollBy({ top: delta, behavior: "smooth" });
      }
    };

    const scheduleReveal = () => {
      if (settleTimer !== undefined) clearTimeout(settleTimer);
      settleTimer = setTimeout(reveal, SETTLE_MS);
    };

    // Keyboard opening shrinks the viewport. Only react to a *shrink* (and track the
    // tallest height as the keyboard-closed baseline) so growth / same-size resizes
    // never trigger a reveal.
    let lastHeight = viewportHeight();
    const onResize = () => {
      const h = viewportHeight();
      const shrank = h < lastHeight - 1;
      lastHeight = h;
      if (h > fullHeight) fullHeight = h;
      if (shrank) scheduleReveal();
    };

    // Caret moved (typing, tap, arrow). A plain scroll doesn't change the selection,
    // so guarding on the head changing is what keeps scrolling from snapping back.
    let lastHead: number | null = null;
    const onSelectionChange = () => {
      const head = doc.editor.tiptap?.state.selection.head ?? null;
      if (head === lastHead) return;
      lastHead = head;
      scheduleReveal();
    };

    window.visualViewport?.addEventListener("resize", onResize);
    document.addEventListener("focusin", scheduleReveal);
    document.addEventListener("selectionchange", onSelectionChange);

    return () => {
      if (settleTimer !== undefined) clearTimeout(settleTimer);
      window.visualViewport?.removeEventListener("resize", onResize);
      document.removeEventListener("focusin", scheduleReveal);
      document.removeEventListener("selectionchange", onSelectionChange);
    };
  }, [doc]);
}
